import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";

import { useTranslation } from "react-i18next";
import { useClickOutside } from "../../../../../hooks";
import { ConfigContext } from "../../../../../utils/ConfigContext";
import { IconButton, Tooltip } from "../../../../UI";
import {
  SectionItem,
  SectionItemContainer,
} from "../UI/Section/Section-styled";
import SidebarDropdown from "../UI/SidebarDropdown/SidebarDropdown";
import { DropdownItem } from "../UI/SidebarDropdown/SidebarDropdown-styled";
import {
  StyledAccountContainer,
  StyledAccountHeader,
  StyledAvatarContainer,
  StyledUserInfoContainer,
} from "./UserAccount-styled";

const MdOutlineLogOut = (props) => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 24 24"
    height="18px"
    width="18px"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path fill="none" d="M0 0h24v24H0V0z"></path>
    <path d="m17 8-1.41 1.41L17.17 11H9v2h8.17l-1.58 1.58L17 16l4-4-4-4zM5 5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h7v-2H5V5z"></path>
  </svg>
);

const MdOutlineLogIn = (props) => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 24 24"
    height="18px"
    width="18px"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path fill="none" d="M0 0h24v24H0z"></path>
    <path d="M11 7 9.6 8.4l2.6 2.6H2v2h10.2l-2.6 2.6L11 17l5-5-5-5zm9 12h-8v2h8c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-8v2h8v14z"></path>
  </svg>
);

const AvatarIcon = (props) => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="0"
    viewBox="0 0 24 24"
    height="18px"
    width="18px"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16 9C16 11.2091 14.2091 13 12 13C9.79086 13 8 11.2091 8 9C8 6.79086 9.79086 5 12 5C14.2091 5 16 6.79086 16 9ZM14 9C14 10.1046 13.1046 11 12 11C10.8954 11 10 10.1046 10 9C10 7.89543 10.8954 7 12 7C13.1046 7 14 7.89543 14 9Z"
      fill="currentColor"
    ></path>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 1C5.92487 1 1 5.92487 1 12C1 18.0751 5.92487 23 12 23C18.0751 23 23 18.0751 23 12C23 5.92487 18.0751 1 12 1ZM3 12C3 14.0902 3.71255 16.014 4.90798 17.5417C6.55245 15.3889 9.14627 14 12.0645 14C14.9448 14 17.5092 15.3531 19.1565 17.4583C20.313 15.9443 21 14.0524 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12ZM12 21C9.84977 21 7.87565 20.2459 6.32767 18.9878C7.59352 17.1812 9.69106 16 12.0645 16C14.4084 16 16.4833 17.1521 17.7538 18.9209C16.1939 20.2191 14.1881 21 12 21Z"
      fill="currentColor"
    ></path>
  </svg>
);

const UserAccount = ({ expanded }) => {
  const [show, setShow] = useState(false);
  const auth = useSelector((state) => state.authUser);
  const { config } = useContext(ConfigContext);
  const { t } = useTranslation("common");
  const ref = useRef(null);
  useClickOutside(ref, () => {
    setShow(false);
  });

  useEffect(() => {
    setShow(false);
  }, [expanded]);

  const {
    isLoading,
    isAuthenticated,
    user,
    signinRedirect,
    signinPopup,
    removeUser,
    signinSilent,
  } = auth || {};

  const onLogin = async () => {
    try {
      await signinPopup({
        state: {
          url: window.location.href,
          search: window.location.search,
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const onLogout = () => {
    removeUser();
  };

  const renderUserName = useCallback(() => {
    if (!user && !user?.profile) return;
    const array = [];
    const { given_name = "", family_name = "" } = user.profile || {};

    if (given_name && given_name.length > 0) {
      array.push(<span key="given_name">{given_name[0]}</span>);
    }

    if (family_name && family_name.length > 0) {
      array.push(<span key="family_name">{family_name[0]}</span>);
    }

    return array;
  }, [user]);

  const userName = useMemo(() => {
    if (!user || !user?.profile?.given_name) return;

    const array = [];
    const { given_name = "", family_name = "" } = user.profile || {};

    if (given_name && given_name.length > 0) {
      array.push(<span key="given_name">{given_name[0]}</span>);
    }

    if (family_name && family_name.length > 0) {
      array.push(<span key="family_name">{family_name[0]}</span>);
    }

    return {
      initials: array,
      fullName: `${given_name} ${family_name}`,
    };
  }, [user]);

  const handleToggleUserAccount = useCallback(() => {
    setShow((prev) => !prev);
  }, []);

  const tooltipContent = useMemo(() => {
    return !expanded
      ? t("screen.widget.Account.name")
      : t("screen.widget.Account.information");
  }, [expanded]);

  if (!auth || !config.userAccountsEnabled) return null;

  return (
    <SectionItemContainer ref={ref} className="onboarding-account">
      <SectionItem selected={show} onClick={handleToggleUserAccount}>
        <Tooltip
          wrapperStyles={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
          content={tooltipContent}
          showOnHover={true}
          placement="right"
          offset={16}
          blurred
          disabled={show}
          padding="8px"
          fontSize="12px"
        ></Tooltip>
        <IconButton>
          {!userName ? (
            <AvatarIcon />
          ) : (
            <StyledAvatarContainer>{userName?.initials}</StyledAvatarContainer>
          )}
        </IconButton>

        {expanded ? (
          <span>
            {!userName ? t("screen.widget.Account.name") : userName?.fullName}
          </span>
        ) : (
          ""
        )}
        {show && (
          <SidebarDropdown>
            <StyledAccountContainer>
              {user && (
                <StyledAccountHeader>
                  <StyledAvatarContainer hasBg size="30px">
                    {userName?.initials}
                  </StyledAvatarContainer>
                  <StyledUserInfoContainer>
                    <span>{userName?.fullName}</span>
                    <span>{user.profile?.sub}</span>
                  </StyledUserInfoContainer>
                </StyledAccountHeader>
              )}
              {userName ? (
                <DropdownItem onClick={onLogout}>
                  <MdOutlineLogOut width="14px" height="14px" />
                  <span>{t("screen.widget.Account.actions.signOut")}</span>
                </DropdownItem>
              ) : (
                <DropdownItem onClick={onLogin}>
                  <MdOutlineLogIn width="14px" height="14px" />
                  <span>{t("screen.widget.Account.actions.signIn")}</span>
                </DropdownItem>
              )}
            </StyledAccountContainer>
          </SidebarDropdown>
        )}
      </SectionItem>
    </SectionItemContainer>
  );
};

export default UserAccount;
